/* Inside Lumetier — at-a-glance products grid. Pixieset-style 4-card row. */

import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { GOLD, GOLD_LIGHT } from "./constants";
import { EDITORIAL_ASSETS } from "@/lib/editorialAssets";

export default function InsideSection() {
  const products = [
    {
      id: "galleries",
      title: "Client galleries",
      tagline: "Proofs · Selections · Delivery",
      body: "Branded galleries with auto-passwords, favorites, downloads, and folders so proofs and edits stay neatly apart.",
      href: "#customize",
      cta: "Open a sample gallery",
      ready: true,
      image: EDITORIAL_ASSETS.editorial_studio_wide,
    },
    {
      id: "website",
      title: "Studio website",
      tagline: "Your front page — booking-ready from day one",
      body: "A boutique site at studio.lumetiercreatives.com/s/your-slug. Sessions, portfolio, contact — all integrated with bookings so a click on \"Book a session\" lands a deposit in your manager.",
      href: "#customize-site",
      cta: "Customize a site",
      ready: true,
      highlight: true,
      image: EDITORIAL_ASSETS.editorial_fabric_motion,
    },
    {
      id: "manager",
      title: "Studio manager",
      tagline: "Bookings · Contracts · Payments · Clients",
      body: "Your admin dashboard. See every session, contract status, deposit paid, and gallery delivered at a glance. No spreadsheet, no 5 tools.",
      href: "#tour",
      cta: "See the manager tour",
      ready: true,
      image: EDITORIAL_ASSETS.editorial_atelier_scene,
    },
    {
      id: "store",
      title: "Print store",
      tagline: "Coming late 2026",
      body: "Sell prints, albums, and digital downloads directly from your client gallery — with print-lab fulfilment and your own markup.",
      href: null,
      cta: "Coming soon",
      ready: false,
      image: EDITORIAL_ASSETS.editorial_detail_hands,
    },
  ];

  return (
    <section id="inside" className="border-t border-white/[0.07] py-28" data-testid="inside-section">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Inside Lumetier</div>
            <h2
              className="text-4xl md:text-5xl font-light text-stone-100"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Four products.{" "}
              <em className="italic" style={{ color: GOLD }}>One weave.</em>
            </h2>
            <p className="text-stone-400 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
              The same database powers all of it. A booking on your website becomes a contract in your manager becomes a gallery for your client. No Zapier. No webhooks. No re-typing.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {products.map((p, i) => {
            const Card = (
              <div
                className={`editorial-card-hover relative h-full p-7 border transition-all flex flex-col ${
                  p.highlight
                    ? "bg-white/[0.06] border-[#C9A961]/40 hover:border-[#C9A961]/70"
                    : p.ready
                    ? "bg-white/[0.03] border-white/[0.08] hover:bg-white/[0.05] hover:border-white/[0.18]"
                    : "bg-white/[0.015] border-white/[0.06] opacity-70"
                }`}
              >
                {p.highlight && (
                  <span
                    className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 z-10"
                    style={{ background: `${GOLD}30`, color: GOLD }}
                  >
                    ✦ Our wedge
                  </span>
                )}
                {!p.ready && (
                  <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-white/[0.18] text-stone-500 z-10">
                    Coming
                  </span>
                )}
                {/* Phase 1 — editorial image header for each product tile.
                    Aspect-locked so layout stays stable as the image loads. */}
                {p.image && (
                  <div className="-mx-7 -mt-7 mb-5 overflow-hidden">
                    <img
                      src={p.image.src}
                      alt={p.image.alt}
                      width={p.image.w}
                      height={p.image.h}
                      loading="lazy"
                      className="w-full aspect-[16/10] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      style={{ transform: "translateZ(0)", filter: p.ready ? "none" : "grayscale(0.4)" }}
                    />
                  </div>
                )}
                <div
                  className="text-2xl font-light text-stone-100 mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {p.title}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-4">
                  {p.tagline}
                </div>
                <p className="text-sm text-stone-400 leading-relaxed flex-1">{p.body}</p>
                <div
                  className={`mt-5 text-xs uppercase tracking-[0.18em] flex items-center gap-1.5 ${
                    p.ready ? "" : "text-stone-600"
                  }`}
                  style={p.ready ? { color: p.highlight ? GOLD_LIGHT : GOLD } : {}}
                >
                  {p.cta}
                  {p.ready && <ArrowRight className="w-3.5 h-3.5" />}
                </div>
              </div>
            );
            return (
              <Reveal key={p.id} delay={i * 0.08}>
                {p.href ? (
                  <a href={p.href} data-testid={`inside-card-${p.id}`} className="block h-full">
                    {Card}
                  </a>
                ) : (
                  <div data-testid={`inside-card-${p.id}`} className="h-full">
                    {Card}
                  </div>
                )}
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
